import { useEffect, useRef, useState } from "react";
import { checkTokenExpiration } from "../utils/commonUtils";
import { useNavigate } from "react-router-dom";
import useAuth from "../Hooks/useAuth";

/**
 * A unified hook to guard against expired sessions.
 * It handles both:
 *  - Immediate check on mount (for returning users)
 *  - Periodic check in background (for active users)
 *
 * @param expTime - Token expiration timestamp (in seconds)
 * @param skipExpiryCheck - Whether the session is being validated on initial application load
 * @returns `isChecking` - Loading state while checking the session validity
 */
const useSessionGuard = (expTime: number | null, skipExpiryCheck = false) => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [isChecking, setIsChecking] = useState(true);
    const hasLoggedOutRef = useRef(false);

    useEffect(() => {

        const checkAndHandleLogout = async () => {
            if (skipExpiryCheck) {
                setIsChecking(false);
                return;
            }

            // Read fresh exp from localStorage to stay in sync with other tabs
            const freshExp = Number(localStorage.getItem("exp")) || expTime;
            const isExpired = checkTokenExpiration(freshExp, 10);

            if (isExpired && !hasLoggedOutRef.current) {
                hasLoggedOutRef.current = true; // prevent multiple triggers
                await logout({ isSessionTimeout: true });
                navigate("/login", { replace: true, state: { sessionExpired: true } });
            } else {
                setIsChecking(false); // done checking once on mount
            }
        };

        // Immediate check
        checkAndHandleLogout();

        // Interval check for active session
        const intervalId = setInterval(checkAndHandleLogout, 5000);

        return () => clearInterval(intervalId);
    }, [expTime, logout, navigate, skipExpiryCheck]);

    return { isChecking };
};

export default useSessionGuard;